import { effectiveGroupThreshold, takesAgentOrders } from "./channel-config.js";
import { membershipsModuleOn } from "./memberships.js";
/**
 * B20b(h) — §4.1/§7 guest sessions: the mint, NOT anonymous operations.
 *
 * A buyer with no credential calls the public `session.guest` door and
 * receives a short-lived signed token bound to a FRESH guest identity;
 * the authorizer resolves it into an ordinary CUSTOMER principal, so every
 * wall built for customers (tenant fence, scopes, audience) applies
 * unchanged from the very next call.
 *
 * Format mirrors §8.1 ticket tokens (ticket-token.ts), distinct version:
 *
 *   gst1.<base64url(canonical payload JSON)>.<keyId>.<base64url(signature)>
 *
 * The signature covers `gst1.<payload-b64>.<keyId>`; canonical JSON means
 * minting is deterministic and the bytes verified are the bytes signed.
 * The signing KEY must be a guest-session key — never the ticket key
 * (separate custody, separate blast radius; a leaked ticket key must not
 * mint identities and vice versa). The distinct version string walls the
 * domains even if custody ever slips: a ttk1 verifier refuses gst1 bytes.
 *
 * Walls at the mint (this module):
 * - RATE LIMIT per (tenant, channel): counted against the append-only
 *   mint log — the log IS the audit trail, so the wall and the evidence
 *   cannot drift. Fail closed: an unavailable log refuses the mint.
 * - TTL: expiry lives INSIDE the signed payload; verification enforces it
 *   with the caller's clock. Default 30 minutes — one §7 purchase, not a
 *   durable identity (sign-in is a separately-ruled door).
 *
 * PURITY: signing/verification/randomness are injected ports.
 */

import type { ChannelConfigStore } from "./channel-config.js";
import type { ConnectAccountStore } from "./connect-custody.js";
import type { AlertPreset } from "./ops-alerts.js";
import type { OpsConfigStore } from "./ops-config.js";
import type { ChannelTheme } from "./theming.js";
import type { TenantType } from "./vocabulary.js";
import type { Clock } from "./clock.js";
import { checkEmbedOrigin } from "./embedding.js";
import { tenantRowKey } from "./keys.js";
import { fromBase64Url, toBase64Url, type TicketSigner, type TicketVerifierPort } from "./ticket-token.js";
import { alertPresetFor } from "./ops-alerts.js";

/* ---------------------------------------------------------------- types */

export interface GuestSessionPayload {
  readonly tenantId: string;
  readonly channelId: string;
  readonly customerId: string;
  readonly issuedAt: number;
  readonly expiresAt: number;
}

export type GuestSessionRefusal = "malformed" | "bad_signature" | "unknown_key" | "expired";

export type VerifiedGuestSession =
  | { readonly ok: true; readonly keyId: string; readonly payload: GuestSessionPayload }
  | { readonly ok: false; readonly why: GuestSessionRefusal };

export class InvalidGuestSessionPayloadError extends Error {
  constructor(why: string) {
    super(`Refusing to mint a guest session: ${why}`);
    this.name = "InvalidGuestSessionPayloadError";
  }
}

/** The flood wall spoke: mints for this (tenant, channel) exceeded the window cap. */
export class GuestMintRateLimitedError extends Error {
  constructor(
    tenantId: string,
    channelId: string,
    /** B103 — which counter spoke: for OUR logs; the wire answer stays uniform. */
    readonly dimension: "channel" | "source" = "channel",
  ) {
    super(`Guest-session mint rate limit reached: tenant=${tenantId} channel=${channelId} dimension=${dimension}; retry later`);
    this.name = "GuestMintRateLimitedError";
  }
}

/**
 * B103 (AC-3) — the transport reported a source but no salted hasher is
 * wired on this stage. Fail closed: an uncountable source must not mint
 * (the per-source wall would silently not exist). Mirrors the origin
 * wall's ORIGIN_WALL_UNWIRED stance.
 */
export class GuestMintSourceWallUnwiredError extends Error {
  constructor(tenantId: string, channelId: string) {
    super(`Guest-session mint refused: source wall unwired: tenant=${tenantId} channel=${channelId}`);
    this.name = "GuestMintSourceWallUnwiredError";
  }
}

/**
 * B31a — the §9.2 origin wall said no. The reason names the wall that
 * refused, NEVER the configured allowlist (the message crosses the wire
 * to an unauthenticated caller).
 */
export type GuestMintOriginRefusal =
  | "ORIGIN_WALL_UNWIRED" // an Origin was presented but no config store is injected — fail closed
  | "CHANNEL_NOT_CONFIGURED" // no channel-config row: an empty allowlist admits nothing
  | "NO_ORIGIN" // present-but-empty Origin value
  | "MALFORMED_ORIGIN" // includes the browser "null" origin (sandboxed frame, file://)
  | "ORIGIN_NOT_ALLOWED";

export class GuestMintOriginRefusedError extends Error {
  constructor(
    tenantId: string,
    channelId: string,
    readonly reason: GuestMintOriginRefusal,
  ) {
    super(`Guest-session mint refused by the origin wall: tenant=${tenantId} channel=${channelId} reason=${reason}`);
    this.name = "GuestMintOriginRefusedError";
  }
}

/* ------------------------------------------------------------- mint log */

/** One row per mint ATTEMPT that passed the wall — the audit trail the rate limit counts. */
export interface GuestMintRecord {
  readonly tenantId: string;
  readonly channelId: string;
  readonly customerId: string;
  readonly requestId: string;
  readonly at: number;
  /**
   * B103 (AC-3) — the SALTED HASH of the transport source, opaque to the
   * domain (the edge hashes; the raw IP never enters the log). Absent for
   * callers with no transport source (stdio, in-process).
   */
  readonly sourceKey?: string;
}

/** B103 — a sourceKey must be usable as a key path segment on every backend. */
export function assertSourceKeyShape(sourceKey: string | undefined): void {
  if (sourceKey === undefined) return;
  if (sourceKey.length === 0 || sourceKey.includes("#"))
    throw new InvalidGuestSessionPayloadError("sourceKey must be non-empty and '#'-free");
}

export interface GuestMintLogStore {
  /** Append-only; no update or delete path exists. */
  append(record: GuestMintRecord): Promise<void>;
  /** Mints for (tenant, channel) with at >= since — the rate-limit window count. */
  countSince(tenantId: string, channelId: string, since: number): Promise<number>;
  /** B103 — mints for (tenant, channel, sourceKey) with at >= since: the per-source window count. */
  countBySourceSince(tenantId: string, channelId: string, sourceKey: string, since: number): Promise<number>;
}

export class InMemoryGuestMintLogStore implements GuestMintLogStore {
  private readonly rows = new Map<string, GuestMintRecord[]>();

  append(record: GuestMintRecord): Promise<void> {
    try {
      assertSourceKeyShape(record.sourceKey);
    } catch (err) {
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
    const key = tenantRowKey(record.tenantId, record.channelId);
    const list = this.rows.get(key) ?? [];
    list.push(record);
    this.rows.set(key, list);
    return Promise.resolve();
  }

  countSince(tenantId: string, channelId: string, since: number): Promise<number> {
    const list = this.rows.get(tenantRowKey(tenantId, channelId)) ?? [];
    return Promise.resolve(list.filter((r) => r.at >= since).length);
  }

  countBySourceSince(tenantId: string, channelId: string, sourceKey: string, since: number): Promise<number> {
    const list = this.rows.get(tenantRowKey(tenantId, channelId)) ?? [];
    return Promise.resolve(list.filter((r) => r.sourceKey === sourceKey && r.at >= since).length);
  }
}

/* -------------------------------------------------------------- payload */

const PAYLOAD_KEYS = ["tenantId", "channelId", "customerId", "issuedAt", "expiresAt"] as const;
const VERSION = "gst1";
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function assertPayload(payload: GuestSessionPayload): void {
  for (const field of ["tenantId", "channelId", "customerId"] as const) {
    const v = payload[field];
    if (typeof v !== "string" || v.trim().length === 0)
      throw new InvalidGuestSessionPayloadError(`${field} is required`);
  }
  for (const field of ["issuedAt", "expiresAt"] as const) {
    const v = payload[field];
    if (!Number.isSafeInteger(v) || v < 0)
      throw new InvalidGuestSessionPayloadError(`${field} must be a non-negative epoch-ms integer`);
  }
  if (payload.expiresAt <= payload.issuedAt)
    throw new InvalidGuestSessionPayloadError("expiresAt must follow issuedAt");
}

/** Canonical form: fixed key order — deterministic bytes (ticket-token discipline). */
function canonicalPayloadJson(payload: GuestSessionPayload): string {
  const ordered: Record<string, unknown> = {};
  for (const key of PAYLOAD_KEYS) ordered[key] = payload[key];
  return JSON.stringify(ordered);
}

function parsePayload(json: string): GuestSessionPayload | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(PAYLOAD_KEYS as readonly string[]).includes(key)) return undefined; // extras (a smuggled role?) are malformed
  }
  const out: Record<string, unknown> = {};
  for (const field of ["tenantId", "channelId", "customerId"] as const) {
    const v = record[field];
    if (typeof v !== "string" || v.trim().length === 0) return undefined;
    out[field] = v;
  }
  for (const field of ["issuedAt", "expiresAt"] as const) {
    const v = record[field];
    if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) return undefined;
    out[field] = v;
  }
  const payload = out as unknown as GuestSessionPayload;
  if (payload.expiresAt <= payload.issuedAt) return undefined;
  return payload;
}

/* ----------------------------------------------------------------- api */

export async function mintGuestSessionToken(payload: GuestSessionPayload, signer: TicketSigner): Promise<string> {
  assertPayload(payload);
  if (signer.keyId.trim().length === 0 || signer.keyId.includes("."))
    throw new InvalidGuestSessionPayloadError("signer keyId must be non-empty and dot-free");
  const body = toBase64Url(encoder.encode(canonicalPayloadJson(payload)));
  const signedBytes = encoder.encode(`${VERSION}.${body}.${signer.keyId}`);
  const signature = await signer.sign(signedBytes);
  return `${VERSION}.${body}.${signer.keyId}.${toBase64Url(signature)}`;
}

/** Structural parse — fail-closed, throw-free, canonical-form-only (no malleable duplicates). */
export function parseGuestSessionToken(token: string):
  | { readonly ok: true; readonly payload: GuestSessionPayload; readonly keyId: string; readonly signedBytes: Uint8Array; readonly signature: Uint8Array }
  | { readonly ok: false; readonly why: "malformed" } {
  const malformed = { ok: false as const, why: "malformed" as const };
  const parts = token.split(".");
  if (parts.length !== 4) return malformed;
  const [version, body, keyId, sig] = parts as [string, string, string, string];
  if (version !== VERSION || body.length === 0 || keyId.length === 0 || sig.length === 0) return malformed;
  const payloadBytes = fromBase64Url(body);
  const signature = fromBase64Url(sig);
  if (payloadBytes === undefined || signature === undefined || signature.length === 0) return malformed;
  const payload = parsePayload(decoder.decode(payloadBytes));
  if (payload === undefined) return malformed;
  if (toBase64Url(encoder.encode(canonicalPayloadJson(payload))) !== body) return malformed;
  return { ok: true, payload, keyId, signedBytes: encoder.encode(`${VERSION}.${body}.${keyId}`), signature };
}

/** Full verification: structure, signature under the named key, and expiry against the caller's clock. */
export async function verifyGuestSessionToken(
  token: string,
  verifier: TicketVerifierPort,
  clock: Clock,
): Promise<VerifiedGuestSession> {
  const parsed = parseGuestSessionToken(token);
  if (!parsed.ok) return { ok: false, why: parsed.why };
  const result = await verifier.verify(parsed.signedBytes, parsed.signature, parsed.keyId);
  if (result !== "ok") return { ok: false, why: result };
  if (clock.now() >= parsed.payload.expiresAt) return { ok: false, why: "expired" };
  return { ok: true, keyId: parsed.keyId, payload: parsed.payload };
}

/* --------------------------------------------------------- orchestration */

/** Defaults: one §7 purchase, not a durable identity; a public door needs a low ceiling. */
export const DEFAULT_GUEST_SESSION_TTL_MS = 30 * 60_000;
export const DEFAULT_GUEST_MINT_WINDOW_MS = 60 * 60_000;

/**
 * B103 (AC-4) — the mint caps, per window. Tenant config in the B80
 * ops-config home with per-preset defaults; sized conservative-but-
 * generous (the B102 stance) until B89's surge baseline lands:
 * - per channel: the old global 100/h starved a genuine on-sale (AC-4);
 *   a popular drop mints far more than 100 buyers an hour.
 * - per source: a real buyer mints a handful of sessions an hour; 30
 *   leaves room for retries and small shared NATs while making channel-
 *   budget exhaustion from one source ~40x more expensive (AC-3).
 */
export interface GuestMintCaps {
  readonly maxPerChannelPerWindow: number;
  readonly maxPerSourcePerWindow: number;
}

export const GUEST_MINT_PRESET_DEFAULTS: Readonly<Record<AlertPreset | "default", GuestMintCaps>> = Object.freeze({
  default: Object.freeze({ maxPerChannelPerWindow: 600, maxPerSourcePerWindow: 30 }),
  ferry: Object.freeze({ maxPerChannelPerWindow: 1200, maxPerSourcePerWindow: 30 }), // sailings cluster departures — bursts are the normal shape
  zoo: Object.freeze({ maxPerChannelPerWindow: 600, maxPerSourcePerWindow: 30 }),
  attraction: Object.freeze({ maxPerChannelPerWindow: 900, maxPerSourcePerWindow: 30 }), // timed-entry drops spike
});

/** Overrides win field-by-field on the preset line — resolveAlertThresholds' own shape. */
export function resolveGuestMintCaps(preset: AlertPreset | undefined, overrides?: Partial<GuestMintCaps>): GuestMintCaps {
  const base = GUEST_MINT_PRESET_DEFAULTS[preset ?? "default"];
  return {
    maxPerChannelPerWindow: overrides?.maxPerChannelPerWindow ?? base.maxPerChannelPerWindow,
    maxPerSourcePerWindow: overrides?.maxPerSourcePerWindow ?? base.maxPerSourcePerWindow,
  };
}

/** Legacy alias — the abuse-controls doc pin reads these names. */
export const DEFAULT_GUEST_MINT_MAX_PER_WINDOW = GUEST_MINT_PRESET_DEFAULTS.default.maxPerChannelPerWindow;
export const DEFAULT_GUEST_MINT_MAX_PER_SOURCE_PER_WINDOW = GUEST_MINT_PRESET_DEFAULTS.default.maxPerSourcePerWindow;

export interface GuestSessionDeps {
  readonly mintLog: GuestMintLogStore;
  readonly signer: TicketSigner;
  readonly clock: Clock;
  /** Fresh, unguessable id suffix — crypto randomness is I/O, minted at the edge. */
  readonly newGuestId: () => string;
  /**
   * B31a §9.2 — the channel-config rows the origin wall checks (the SAME
   * rows channel.config.set writes: one list, both walls). Optional only
   * for callers that never see a browser; a mint that carries an Origin
   * while this is unwired refuses (fail closed).
   */
  readonly channelConfigs?: Pick<ChannelConfigStore, "get">;
  /**
   * B103 (AC-4) — the B80 ops-config home: the tenant's preset + guestMint
   * overrides drive the caps. Optional; absent = preset-default line.
   */
  readonly opsConfigs?: Pick<OpsConfigStore, "get">;
  /**
   * B103 (AC-3) — the salted source hasher, injected at the edge (the salt
   * is per-stage; the domain sees neither salt nor raw IP semantics). A
   * mint carrying a source while this is unwired REFUSES (fail closed).
   */
  readonly sourceKeyOf?: (source: string) => string;
  readonly ttlMs?: number;
  readonly rateWindowMs?: number;
  /** Explicit injection seams — they win over tenant config when set. */
  readonly maxMintsPerWindow?: number;
  readonly maxMintsPerSourcePerWindow?: number;
  /**
   * B91 GAP-6 — Connect custody. When the tenant's connected account is
   * onboarded, the mint carries it so the payment surface mounts Stripe.js
   * on the account the intent will live on. Optional; absent = platform path.
   */
  readonly connectAccounts?: Pick<ConnectAccountStore, "get">;
}

export interface MintGuestSessionCommand {
  readonly tenantId: string;
  readonly channelId: string;
  readonly requestId: string;
  /**
   * The browser Origin header, verbatim, when the transport saw one.
   * undefined = no Origin was sent (a server-side caller, not a browser):
   * the origin wall is a browser-protection wall (CSRF class) and does not
   * apply — the rate limit and the mint log govern those callers alone.
   */
  readonly origin?: string;
  /**
   * B103 — the transport source (client IP), verbatim, when the transport
   * saw one. undefined = no transport source (stdio, in-process): the
   * per-source wall does not apply — those callers are governed by the
   * channel counter and the audit log alone.
   */
  readonly source?: string;
}

export interface MintedGuestSession {
  readonly token: string;
  readonly customerId: string;
  readonly expiresAt: number;
  /**
   * B52 — the channel's brand theme, riding the buyer's FIRST call (the
   * delivery seam decision: the mint already reads the channel-config row
   * for the origin wall; no new public door). Absent when unconfigured.
   */
  readonly theme?: ChannelTheme;
  /**
   * B397 leg (3) — the channel's provider publishable key, same seam and
   * same honest absence as the theme above. Public by design (it is the
   * key Stripe documents for embedding in public pages, and intent.create
   * already vends it to the same caller).
   */
  readonly publishableKey?: string;
  /**
   * B549 — the channel's EFFECTIVE group threshold (default 20; absent
   * here = OFF): the buyer surface's stepper cap and interrupt cue, from
   * the same config row the mint already reads.
   */
  readonly groupThreshold?: number;
  /** B549 (9): the groups desk's first name — only beside a live threshold. */
  readonly groupsContactName?: string;
  /** B1085: false = the channel takes no agent's order (the page declares no buyer tools); absent = on. */
  readonly agentOrders?: false;
  /**
   * B403 #26 — the tenant's stored business name (75:2 draws it in the
   * storefront header and footer). From the SAME ops-config row the caps
   * already read; honest absence when the tenant never stored one.
   */
  readonly businessName?: string;
  /** B653 (c) — where guests find the venue, from the same row; honest absence each. */
  readonly venueAddress?: string;
  readonly venuePhone?: string;
  readonly venueNote?: string;
  /**
   * B403 #22-extension — the tenant's stored TYPE (ops-config's B126
   * fact), so buyer sentences speak the tenant's own vocabulary pack
   * ("on the same sailing"). Public by nature: the words already reach
   * every anonymous buyer. Honest absence when the tenant never chose.
   */
  readonly tenantType?: TenantType;
  /** B524 leg 2 — the tenant's GTM container (the hosted page loads it); same row, same honest absence. */
  readonly gtmContainerId?: string;
  /**
   * B818 — the memberships MODULE is on for this tenant (the same ops-config
   * row; B725's switch, defaulting on for every type but SHOW). The buyer
   * surface draws its MEMBERSHIPS door only when a plan is also on sale —
   * that read is the page's, under the token this mint hands it.
   */
  readonly memberships?: true;
  /**
   * B621 — the tenant's CLOCK facts (RULED 2026-10-01: 12h AM/PM default,
   * tenant can choose 24h), riding the same ops-config row so the buyer
   * face shows the tenant's own zone and format from its FIRST call.
   * Public by nature — they shape every time an anonymous buyer already
   * reads. Honest absence when never stored; 12h is the READER's default.
   */
  readonly timezone?: string;
  readonly timeFormat?: "12h" | "24h";
  /**
   * B91 GAP-6 — the tenant's ONBOARDED connected account (acct_…), when
   * one exists: Stripe.js must be constructed on it for the deferred
   * element to confirm the intents payment.intent.create now routes there.
   * Public by nature (it rides every payment page's boot); honest absence
   * otherwise.
   */
  readonly stripeAccount?: string;
}

/**
 * The mint flow: rate-limit wall first (counted against the audit log, so
 * wall and evidence cannot drift), then the row, then the token. The log
 * write precedes the token leaving the building — a mint that cannot be
 * audited does not happen.
 */
export async function mintGuestSession(
  deps: GuestSessionDeps,
  cmd: MintGuestSessionCommand,
): Promise<MintedGuestSession> {
  if (cmd.tenantId.trim().length === 0) throw new InvalidGuestSessionPayloadError("tenantId is required");
  if (cmd.channelId.trim().length === 0) throw new InvalidGuestSessionPayloadError("channelId is required");

  // B31a §9.2 — the origin wall, FIRST (pinned): a browser presenting an
  // Origin must be on the channel's allowlist before anything else is
  // spent on it. No config row = no allowlist = nothing admits (fail
  // closed; a live buyer stage configures its channel via
  // channel.config.set, including the hosted storefront's own origin).
  // B52 — ONE read serves both walls and the theme: the same row the origin
  // wall checks carries the brand the storefront paints with.
  const configRow = deps.channelConfigs === undefined ? undefined : await deps.channelConfigs.get(cmd.tenantId, cmd.channelId);
  if (cmd.origin !== undefined) {
    if (deps.channelConfigs === undefined) {
      throw new GuestMintOriginRefusedError(cmd.tenantId, cmd.channelId, "ORIGIN_WALL_UNWIRED");
    }
    if (configRow === undefined) {
      throw new GuestMintOriginRefusedError(cmd.tenantId, cmd.channelId, "CHANNEL_NOT_CONFIGURED");
    }
    const verdict = checkEmbedOrigin(configRow.config, cmd.origin);
    if (!verdict.allowed) throw new GuestMintOriginRefusedError(cmd.tenantId, cmd.channelId, verdict.reason);
  }

  // B103 (AC-3) — hash the transport source FIRST (fail closed when the
  // hasher is unwired): an uncountable source must not reach the counters.
  let sourceKey: string | undefined;
  if (cmd.source !== undefined) {
    if (deps.sourceKeyOf === undefined) throw new GuestMintSourceWallUnwiredError(cmd.tenantId, cmd.channelId);
    sourceKey = deps.sourceKeyOf(cmd.source);
    assertSourceKeyShape(sourceKey);
  }

  const now = deps.clock.now();
  const windowMs = deps.rateWindowMs ?? DEFAULT_GUEST_MINT_WINDOW_MS;
  // B103 (AC-4) — caps: explicit injection seam > tenant config (B80 home) > preset defaults.
  const opsRow = deps.opsConfigs === undefined ? undefined : await deps.opsConfigs.get(cmd.tenantId);
  // B126(f): the standing signal is preset ?? the tenant-TYPE derivation —
  // the same line ops-config echoes, so the door and the echo cannot drift.
  const resolved = resolveGuestMintCaps(
    opsRow?.config.preset ?? alertPresetFor(opsRow?.config.tenantType),
    opsRow?.config.guestMint,
  );
  const channelCap = deps.maxMintsPerWindow ?? resolved.maxPerChannelPerWindow;
  const sourceCap = deps.maxMintsPerSourcePerWindow ?? resolved.maxPerSourcePerWindow;

  if (sourceKey !== undefined) {
    const recentBySource = await deps.mintLog.countBySourceSince(cmd.tenantId, cmd.channelId, sourceKey, now - windowMs);
    if (recentBySource >= sourceCap) throw new GuestMintRateLimitedError(cmd.tenantId, cmd.channelId, "source");
  }
  const recent = await deps.mintLog.countSince(cmd.tenantId, cmd.channelId, now - windowMs);
  if (recent >= channelCap) throw new GuestMintRateLimitedError(cmd.tenantId, cmd.channelId, "channel");

  const customerId = `guest-${deps.newGuestId()}`;
  await deps.mintLog.append({
    tenantId: cmd.tenantId,
    channelId: cmd.channelId,
    customerId,
    requestId: cmd.requestId,
    at: now,
    ...(sourceKey === undefined ? {} : { sourceKey }),
  });
  const expiresAt = now + (deps.ttlMs ?? DEFAULT_GUEST_SESSION_TTL_MS);
  const token = await mintGuestSessionToken(
    { tenantId: cmd.tenantId, channelId: cmd.channelId, customerId, issuedAt: now, expiresAt },
    deps.signer,
  );
  const theme = configRow?.config.theme;
  const publishableKey = configRow?.config.providerPublishableKey;
  // B549 — the effective threshold rides the mint; 0/off is an absence.
  const effectiveThreshold = effectiveGroupThreshold(configRow?.config);
  const groupThreshold = effectiveThreshold > 0 ? effectiveThreshold : undefined;
  // B549 (9) — the desk's name signs the interrupt's promise; no interrupt, nothing to sign.
  const groupsContactName = groupThreshold === undefined ? undefined : configRow?.config.groupsContactName;
  // B1085 — the AI agents switch, from the same row; on is an honest absence.
  const agentsOff = !takesAgentOrders(configRow?.config);
  // B91 GAP-6 — onboarded custody rides the same first call.
  const custodyRow = deps.connectAccounts === undefined ? undefined : await deps.connectAccounts.get(cmd.tenantId);
  const stripeAccount = custodyRow !== undefined && custodyRow.record.chargesEnabled ? custodyRow.record.accountId : undefined;
  const businessName = opsRow?.config.businessName;
  const venueAddress = opsRow?.config.venueAddress;
  const venuePhone = opsRow?.config.venuePhone;
  const venueNote = opsRow?.config.venueNote;
  const tenantType = opsRow?.config.tenantType;
  const gtmContainerId = opsRow?.config.gtmContainerId;
  const memberships = membershipsModuleOn(opsRow?.config); // B818
  // B621 — the clock facts ride the same read.
  const timezone = opsRow?.config.timezone;
  const timeFormat = opsRow?.config.timeFormat;
  return {
    token,
    customerId,
    expiresAt,
    ...(theme === undefined ? {} : { theme }),
    ...(groupThreshold === undefined ? {} : { groupThreshold }),
    ...(groupsContactName === undefined ? {} : { groupsContactName }),
    ...(agentsOff ? { agentOrders: false as const } : {}),
    ...(publishableKey === undefined ? {} : { publishableKey }),
    ...(stripeAccount === undefined ? {} : { stripeAccount }),
    ...(businessName === undefined ? {} : { businessName }),
    ...(venueAddress === undefined ? {} : { venueAddress }),
    ...(venuePhone === undefined ? {} : { venuePhone }),
    ...(venueNote === undefined ? {} : { venueNote }),
    ...(tenantType === undefined ? {} : { tenantType }),
    ...(gtmContainerId === undefined ? {} : { gtmContainerId }),
    ...(memberships ? { memberships: true } : {}),
    ...(timezone === undefined ? {} : { timezone }),
    ...(timeFormat === undefined ? {} : { timeFormat }),
  };
}
